import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { captureException } from '@sentry/react';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import uploadPhotoAsync from '@/utilities/uploadPhotoAsync';
import {
    BULK_UPSERT_ITEM_SKUS,
    DELETE_ITEM_SKU,
    DELETE_SKU_DOCUMENT,
    INSERT_ITEM_SKU,
    INSERT_SKU_DOCUMENTS,
    UPDATE_ITEM_SKU,
} from './graphql/mutations';

const SKU_DOCUMENT_PATH = 'sku_documents';
const IMPORT_CHUNK_SIZE = 500;

// Best-effort blob removal. The DB row is the source of truth; if the blob is
// already gone we still want the row deleted, so failures are logged, not thrown.
const deleteBlobByUrl = async (link) => {
    if (!link) return;
    try {
        await deleteObject(ref(getStorage(), link));
    } catch (e) {
        console.error('Failed to delete SKU file from storage', e);
    }
};

// Returns the doc row payload on success, or null if the upload failed
// (uploadPhotoAsync swallows its own errors and resolves undefined).
const uploadSkuFile = async (file) => {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const uri = URL.createObjectURL(file);
    try {
        const link = await uploadPhotoAsync(uri, SKU_DOCUMENT_PATH, ext);
        return link ? { name: file.name, link, file_type: ext } : null;
    } finally {
        URL.revokeObjectURL(uri);
    }
};

export const useSkuMutations = (clientRef) => {
    const [insertSku] = useMutation(INSERT_ITEM_SKU);
    const [updateSku] = useMutation(UPDATE_ITEM_SKU);
    const [deleteSkuMutation] = useMutation(DELETE_ITEM_SKU);
    const [bulkUpsertSkus] = useMutation(BULK_UPSERT_ITEM_SKUS);
    const [insertSkuDocuments] = useMutation(INSERT_SKU_DOCUMENTS);
    const [deleteSkuDocument] = useMutation(DELETE_SKU_DOCUMENT);

    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [importing, setImporting] = useState(false);

    // File ops are best-effort and never roll back the already-committed SKU row;
    // they report how many failed so the caller can warn the user.
    const syncFiles = useCallback(
        async (itemDimensionId, newFiles = [], removedDocs = []) => {
            let failedUploads = 0;
            let failedRemovals = 0;

            if (itemDimensionId && newFiles.length) {
                const results = await Promise.all(newFiles.map(uploadSkuFile));
                const uploaded = results
                    .filter(Boolean)
                    .map((doc) => ({ item_dimension_id: itemDimensionId, ...doc }));
                failedUploads += results.length - uploaded.length;
                if (uploaded.length) {
                    try {
                        await insertSkuDocuments({ variables: { objects: uploaded } });
                    } catch (e) {
                        console.error(e);
                        captureException(e);
                        failedUploads += uploaded.length;
                    }
                }
            }

            for (const doc of removedDocs) {
                try {
                    await deleteBlobByUrl(doc.link);
                    await deleteSkuDocument({
                        variables: { item_dimension_document_id: doc.item_dimension_document_id },
                    });
                } catch (e) {
                    console.error(e);
                    captureException(e);
                    failedRemovals += 1;
                }
            }

            return { failedUploads, failedRemovals };
        },
        [insertSkuDocuments, deleteSkuDocument]
    );

    // Throws only if the SKU row insert/update fails, so the caller can keep the
    // modal open for a retry. Once the row is committed, file failures are returned
    // (not thrown) — a failed file upload must not strand a half-created SKU.
    const saveSku = useCallback(
        async ({ values, newFiles = [], removedDocs = [], existing = null }) => {
            setSaving(true);
            try {
                let itemDimensionId;
                if (existing) {
                    await updateSku({ variables: { item_dimension_id: existing.item_dimension_id, set: values } });
                    itemDimensionId = existing.item_dimension_id;
                } else {
                    const res = await insertSku({ variables: { object: { client_id: clientRef, ...values } } });
                    itemDimensionId = res?.data?.insert_item_dimensions_one?.item_dimension_id;
                }
                // No id back from the insert means the files can't be attached — report
                // them as failures instead of a false success.
                const fileResult = itemDimensionId
                    ? await syncFiles(itemDimensionId, newFiles, removedDocs)
                    : { failedUploads: newFiles.length, failedRemovals: removedDocs.length };
                return { itemDimensionId, ...fileResult };
            } finally {
                setSaving(false);
            }
        },
        [clientRef, insertSku, updateSku, syncFiles]
    );

    const deleteSku = useCallback(
        async (row) => {
            setDeleting(true);
            try {
                // Row first: if the delete fails, the document links keep working.
                // Blob cleanup afterward is best-effort.
                await deleteSkuMutation({ variables: { item_dimension_id: row.item_dimension_id } });
                await Promise.all((row.documents || []).map((doc) => deleteBlobByUrl(doc.link)));
            } finally {
                setDeleting(false);
            }
        },
        [deleteSkuMutation]
    );

    // Chunked so a large CSV doesn't become one oversized mutation/payload.
    const importSkus = useCallback(
        async (rows, updateColumns = []) => {
            setImporting(true);
            try {
                let affected = 0;
                for (let i = 0; i < rows.length; i += IMPORT_CHUNK_SIZE) {
                    const objects = rows
                        .slice(i, i + IMPORT_CHUNK_SIZE)
                        .map((row) => ({ client_id: clientRef, ...row }));
                    const res = await bulkUpsertSkus({ variables: { objects, update_columns: updateColumns } });
                    affected += res?.data?.insert_item_dimensions?.affected_rows ?? 0;
                }
                return affected;
            } finally {
                setImporting(false);
            }
        },
        [clientRef, bulkUpsertSkus]
    );

    return { saveSku, deleteSku, importSkus, saving, deleting, importing };
};
